import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {GetVehicleApiResponse} from '@dms/api/vehicle';
import type {VehicleCatalogueEnumOption} from '@dms/teas';

function flattenFeatureOptions(
  options: readonly VehicleCatalogueEnumOption[]
): VehicleCatalogueEnumOption[] {
  return options.flatMap((option) => [option, ...flattenFeatureOptions(option.children ?? [])]);
}

function categoryKeys(category: VehicleCatalogueEnumOption): string[] {
  return [category.value, ...(category.children ?? []).flatMap(categoryKeys)];
}

export function getAdvertisementPreviewFeatureGroups(
  vehicle: GetVehicleApiResponse,
  categories: VehicleCatalogueEnumOption[],
  featureOptions: VehicleCatalogueEnumOption[]
) {
  const allFeatures = flattenFeatureOptions(featureOptions);
  const selected = vehicle.features ?? [];
  const selectedByKey = new Map(selected.map((feature) => [feature.key, feature]));
  const hiddenParents = new Set(
    allFeatures
      .filter((feature) =>
        feature.children?.some((child) =>
          flattenFeatureOptions([child]).some((descendant) => selectedByKey.has(descendant.value))
        )
      )
      .map((feature) => feature.value)
  );
  const visibleFeatures = allFeatures.filter(
    (feature) => selectedByKey.has(feature.value) && !hiddenParents.has(feature.value)
  );
  const assigned = new Set<string>();

  return categories
    .map((category) => {
      const keys = new Set(categoryKeys(category));
      const features = visibleFeatures.flatMap((feature) => {
        if (
          assigned.has(feature.value) ||
          !feature.categories?.some((item) =>
            keys.has(typeof item === 'string' ? item : item.value)
          )
        ) {
          return [];
        }
        assigned.add(feature.value);
        const translation = selectedByKey.get(feature.value)?.translation;
        return [
          {value: feature.value, label: isNotNilOrEmpty(translation) ? translation : feature.label},
        ];
      });
      return {key: category.value, label: category.label, features};
    })
    .filter((group) => group.features.length > 0);
}
